import lodashSnakeCase from 'lodash-es/snakeCase';
import lodashUniq from 'lodash-es/uniq';

import { formatMileage, formatTimeRemaining } from './format';

import {
    type OrderEntity,
    type OrderEntityOrderItemsItem,
    OrderItemKind,
    type SaleAssetBusEntity,
    type SaleAssetBusListingItem,
    SaleType,
    type UserMeta,
    ValueAddedServiceCode,
} from '~/apiClient';
import { isAuction, isPlatformSaleListingItemEntity, type PlatformSaleItemEntity, type PlatformSaleListingItemEntity } from '~/types/sale.type';

type TrackingVehicle = {
    vin: string;
    first_registration: string | undefined;
    mileage: string;
    engine: string | undefined;
    condition: string | null;
    model: string;
};

type TrackingService = {
    delivery_destination: string;
    temporary_license_plate: string;
    nationalization: string;
    registration: string;
    powertrain_warranty: string;
    fleequid_care: string;
    value: number;
};

export type TrackingSale = {
    id: string;
    name: string;
    brand: string;
    classes: string | null;
    type: string;
    status: string;
    bundle: string;
    list_name: string | null;
    value: number | null;
    currency: string;
    index: number;
    reserve_price_reached: string | null;
    time_remaining: string | null;
    negotiation: string | null;
    selling_method: string;
    vehicles: TrackingVehicle[];
    services?: TrackingService[];
};

type FormatSaleOptions = { value?: number; addServices?: OrderEntity };

const formatVehicleCondition = (asset: SaleAssetBusEntity | SaleAssetBusListingItem, saleType: SaleType): string | null => {
    if (getSaleTypeInfo(saleType).isToBeScraped) return 'to_be_scrapped';

    if (asset.showBadgeInspectionExpired) return 'inspection_expired';

    if (asset.showBadgeInspectionExpiringSoon) return 'inspection_expiring_soon';

    return null;
};

const formatSaleName = (sale: PlatformSaleItemEntity | PlatformSaleListingItemEntity): string => {
    const { isBundleMono, isBundleMulti } = getSaleTypeInfo(sale.type);
    const nAssets = isPlatformSaleListingItemEntity(sale) ? sale.assetCount : sale.assets.length;

    if (isBundleMulti) return `Bundle of ${nAssets} vehicles`;

    const { mainAsset } = sale;

    return `${isBundleMono ? `N. ${nAssets} ` : ''}${mainAsset.brand.brandValue} ${mainAsset.model.modelValue}`;
};

const formatSaleClasses = (sale: PlatformSaleItemEntity | PlatformSaleListingItemEntity): string | null => {
    const classes = 'assets' in sale ? sale.assets.map(asset => asset.class) : [sale.mainAsset.class];
    const uniqueNonNullClasses = lodashUniq(classes).filter(Boolean);

    return uniqueNonNullClasses.length ? uniqueNonNullClasses.join(' | ') : null;
};

const formatReservePriceReached = (userMeta: UserMeta | undefined): string | null =>
    typeof userMeta?.reservePriceReached === 'boolean' ? String(userMeta.reservePriceReached) : null;

const formatVehicle = (asset: SaleAssetBusEntity | SaleAssetBusListingItem, saleType: SaleType): TrackingVehicle => {
    const dayjs = useDayjs();

    return {
        vin: asset.vin,
        first_registration: asset.firstRegistration ? dayjs(asset.firstRegistration).format('MM/YYYY') : undefined,
        mileage: formatMileage(asset.km),
        engine: asset.euroNorm ? lodashSnakeCase(asset.euroNorm) : undefined,
        condition: formatVehicleCondition(asset, saleType),
        model: asset.model.modelValue,
    };
};

type VasOrderItem = Extract<OrderEntityOrderItemsItem, { kind: OrderItemKind.VAS }>;

const isVasOrderItem = (item: OrderEntityOrderItemsItem): item is VasOrderItem => item.kind === OrderItemKind.VAS;

const formatVasServices = (order: OrderEntity): TrackingService => {
    const vasItems = order.orderItems.filter(isVasOrderItem);
    const has = (code: ValueAddedServiceCode): string => String(vasItems.some(item => item.valueAddedServiceCode === code));

    return {
        delivery_destination: has(ValueAddedServiceCode.LogisticsService),
        temporary_license_plate: has(ValueAddedServiceCode.ProvisionalLicensePlate),
        nationalization: has(ValueAddedServiceCode.VehicleNationalization),
        registration: has(ValueAddedServiceCode.VehicleRegistration),
        powertrain_warranty: has(ValueAddedServiceCode.PowertrainWarranty),
        fleequid_care: has(ValueAddedServiceCode.FleequidCare),
        value: vasItems.reduce((sum, item) => sum + item.price, 0),
    };
};

export const formatSale = (
    sale: PlatformSaleItemEntity | PlatformSaleListingItemEntity,
    index: number,
    listName: string | null,
    options: FormatSaleOptions = {}
): TrackingSale => ({
    id: sale.id,
    name: formatSaleName(sale),
    brand: sale.mainAsset.brand.brandValue,
    classes: formatSaleClasses(sale),
    type: lodashSnakeCase(sale.mainAsset.assetType),
    status: lodashSnakeCase(sale.state),
    bundle: String(getSaleTypeInfo(sale.type).isBundle),
    list_name: listName,
    value: options.value ?? (isAuction(sale) ? sale.currentPrice : sale.price) ?? null,
    currency: sale.currency,
    index: index + 1,
    ...(isAuction(sale)
        ? {
              reserve_price_reached: formatReservePriceReached(sale.userMeta),
              time_remaining: sale.expiresAt ? formatTimeRemaining(sale.expiresAt) : null,
              negotiation: String(!!sale.negotiation),
          }
        : {
              reserve_price_reached: null,
              time_remaining: null,
              negotiation: String(!!sale.negotiations?.length),
          }),
    selling_method: lodashSnakeCase(sale.sellingMethod),
    vehicles: 'assets' in sale ? sale.assets.map(asset => formatVehicle(asset, sale.type)) : [formatVehicle(sale.mainAsset, sale.type)],
    ...(options.addServices ? { services: [formatVasServices(options.addServices)] } : {}),
});
